<template>
<div>
<v-expansion-panels
    v-if="parse != null"
    class="panels"
>
    <v-expansion-panel>
        <v-expansion-panel-header>
            Style: {{label}}
        </v-expansion-panel-header>
        <v-expansion-panel-content>

            <m-edit-css-property
                v-for="(item, index) in parse.properties"
                :key="'_prop_'+index"
                :label="item.name"
                :value="item"
                v-on:modify="onModify"
            />

            <!-- Easy CSS Utilities -->
            <div class="mb-3">
                <div class="subtitle-2 mb-2">
                    <v-icon small left color="primary">mdi-magic-staff</v-icon>
                    Quick Style Actions
                </div>
                <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                    <v-btn
                        small
                        outlined
                        color="primary"
                        v-on:click="onStyleOp('add-thick-border')"
                    >
                        <v-icon small left>mdi-border-all-variant</v-icon>
                        Thick Border
                    </v-btn>

                    <v-btn
                        small
                        outlined
                        color="primary"
                        v-on:click="onStyleOp('add-thin-border')"
                    >
                        <v-icon small left>mdi-border-all</v-icon>
                        Thin Border
                    </v-btn>

                    <v-btn
                        small
                        outlined
                        color="primary"
                        v-on:click="onStyleOp('square-corners')"
                    >
                        <v-icon small left>mdi-square-outline</v-icon>
                        Square Corners
                    </v-btn>

                    <v-btn
                        small
                        outlined
                        color="primary"
                        v-on:click="onStyleOp('round-corners')"
                    >
                        <v-icon small left>mdi-rounded-corner</v-icon>
                        Round Corners
                    </v-btn>

                    <v-btn
                        small
                        outlined
                        color="primary"
                        v-on:click="onStyleOp('add-shadow')"
                    >
                        <v-icon small left>mdi-image-filter-drama</v-icon>
                        Add Shadow
                    </v-btn>

                    <v-btn
                        small
                        outlined
                        color="primary"
                        v-on:click="onStyleOp('add-padding')"
                    >
                        <v-icon small left>mdi-format-indent-increase</v-icon>
                        Add Padding
                    </v-btn>
                </div>
            </div>

            <div
                style="text-align: right"
            >
                <v-btn
                    fab
                    dark
                    color="primary"
                    style="margin-right: 15px"
                    v-on:click="onClickReset"
                >
                    <v-icon>
                        mdi-restore
                    </v-icon>
                </v-btn>

                <v-btn
                    fab
                    dark
                    color="primary"
                    v-on:click="onClickAppend"
                >
                    <v-icon>
                        mdi-plus
                    </v-icon>
                </v-btn>
            </div>

        </v-expansion-panel-content>
    </v-expansion-panel>

</v-expansion-panels>

</div>
</template>

<script>
export default {
    props: {
        label: {
            type: String,
            default: function() { return 'Label'; }
        },
        value: {
            type: String,
            default: function() { return ''; }
        },
        default_value: {
            type: String,
            default: function() { return ''; }
        }
    },
    
    data() {
        return {
            parse: null,
            retry_count: 0,
        }
    },

    created () {
        this.queueParseValue();
    },

    methods: {
        queueParseValue() {
            // Add this component to the global CSS parsing queue
            // This avoids hammering the server when multiple components are created at the same time. If they all go together the server rejects some.
            if (!window.css_parse_queue) {
                window.css_parse_queue = [];
                window.css_parse_in_progress = false;
            }

            window.css_parse_queue.push(this);

            // Start processing if not already in progress
            if (!window.css_parse_in_progress) {
                this.process_queue();
            }
        },

        process_queue() {
            if (window.css_parse_queue.length === 0) {
                window.css_parse_in_progress = false;
                return;
            }

            window.css_parse_in_progress = true;
            const component = window.css_parse_queue.shift();

            component.parseValue();
        },

        parseValue()
        {
            const timeout_id = setTimeout(() => {
                this.handleParseComplete();
                // Force a retry on timeout
                if (this.retry_count < 3) {
                    this.retry_count++;
                    setTimeout(() => {
                        this.queueParseValue();
                    }, 1000);
                } else {
                    // Create minimal parse structure to show empty component
                    this.parse = {
                        properties: []
                    };
                }
            }, 8000);

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/css/inline/parse',

                'params' : {
                        'inline_style': this.value,
                        'default_inline_style': this.default_value,
                    },

                'on_success': (data) => {
                        clearTimeout(timeout_id);
                        this.parse = data;
                        this.retry_count = 0;
                        this.handleParseComplete();
                    },

                'on_error': () => {
                        clearTimeout(timeout_id);

                        // Retry up to 3 times with increasing delays
                        if (this.retry_count < 3) {
                            this.retry_count++;
                            const delay = this.retry_count * 500; // 500ms, 1s, 1.5s delays
                            setTimeout(() => {
                                this.parseValue();
                            }, delay);
                        } else {
                            this.parse = {
                                properties: []
                            };
                            this.handleParseComplete();
                        }
                    },
            });
        },

        handleParseComplete() {
            // Process next item in queue after a short delay
            setTimeout(() => {
                this.process_queue();
            }, 100);
        },

        onModify(operation)
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/css/inline/modify',

                'params' : {
                    'inline_style': this.value,
                    'default_inline_style': this.default_value,
                    ...operation,
                },

                'on_success': (data) => {
                        this.parse = data;
                        this.$emit('input', data.inline_style);
                    },

                'on_error': 'Unable to modify styling',
            });
        },

        onClickReset()
        {
            this.$rc.confirm({
                'title': 'Reset Styling: '+this.label,
                'body': 'Do you want to reset the styling to the default?<br/><br/>This can not be undone.',
                'btn_text_ok': 'Reset',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doReset();
                },
            });
        },

        doReset()
        {
            this.value = this.default_value;
            this.$emit('input', this.value);
        },

        onClickAppend()
        {
            this.$rc.prompt({
                'title': 'Add CSS',
                'label': 'CSS to append',
                'value': '',
                'unset_label': null,

                'after_prompt': (value) => {
                    this.onModify({
                        'op': 'append',
                        'value': value
                    });
                },
            });
        },

        onStyleOp(operation)
        {
            this.onModify({
                'op': operation
            });
        }
    },

    watch: {
        value() {
            this.retry_count = 0; // Reset retry count when value changes
            this.queueParseValue();
        }
    },
}
</script>

<style lang="scss" scoped>
.panels
{
    margin-bottom: 15px; 
    border-left: 1px solid rgba(0,0,0,0.1); 
    border-top: 1px solid rgba(0,0,0,0.1);
}
</style>